import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const fieldStyle = { display: 'grid', gap: 4, fontSize: 14 };
const inputStyle = { padding: '6px 8px' };

function errorMessage(error, fallback) {
  const message = error?.response?.data?.error || error?.response?.data?.message || error?.message;
  return typeof message === 'string' ? message : fallback;
}

function serializeSettings(settings) {
  const start = new Date(settings?.interval?.start);
  const end = new Date(settings?.interval?.end);
  const siteId = Number(settings?.siteId);
  if (!Number.isSafeInteger(siteId) || siteId <= 0) {
    throw new Error('Choose a site before saving a view.');
  }
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start >= end) {
    throw new Error('Choose a valid date interval with the start before the end.');
  }
  const channelIds = [...new Set((settings.channelIds || []).map(Number))];
  if (!channelIds.length || channelIds.length > 500
    || channelIds.some(id => !Number.isSafeInteger(id) || id <= 0)) {
    throw new Error('Select between 1 and 500 channels before saving a view.');
  }
  return {
    version: 1,
    siteId,
    channelIds,
    interval: { start: start.toISOString(), end: end.toISOString() },
    showPsd: Boolean(settings.showPsd),
    showLegends: Boolean(settings.showLegends),
  };
}

/** Saves the current plot settings and revalidates a saved view before applying it. */
export default function PlotViewComponent({
  backendUrl,
  user,
  currentSettings,
  onApply,
  onApplyingChange,
  disabled = false,
}) {
  const [views, setViews] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState('Loading saved views...');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [listFailed, setListFailed] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const requestRef = useRef({ sequence: 0, controller: null });
  const accountRef = useRef({ backendUrl, token: user.token });
  const onApplyRef = useRef(onApply);
  const onApplyingChangeRef = useRef(onApplyingChange);
  const applyingRef = useRef(false);
  accountRef.current = { backendUrl, token: user.token };
  onApplyRef.current = onApply;
  onApplyingChangeRef.current = onApplyingChange;

  const setApplying = useCallback((value) => {
    if (applyingRef.current === value) return;
    applyingRef.current = value;
    onApplyingChangeRef.current?.(value);
  }, []);

  const beginRequest = useCallback((message) => {
    requestRef.current.controller?.abort();
    const controller = new AbortController();
    const sequence = requestRef.current.sequence + 1;
    requestRef.current = { sequence, controller };
    setBusy(message);
    setError('');
    setNotice('');
    return {
      options: { headers: { Authorization: `Bearer ${user.token}` }, signal: controller.signal },
      isCurrent: () => requestRef.current.sequence === sequence
        && accountRef.current.backendUrl === backendUrl
        && accountRef.current.token === user.token,
    };
  }, [backendUrl, user.token]);

  const loadViews = useCallback(async () => {
    const request = beginRequest('Loading saved views...');
    setListFailed(false);
    if (!user.token) {
      setBusy('');
      return;
    }
    try {
      const response = await axios.get(`${backendUrl}/plot-views`, request.options);
      if (request.isCurrent()) setViews(response.data);
    } catch (err) {
      if (request.isCurrent()) {
        setError(errorMessage(err, 'Could not load saved views.'));
        setListFailed(true);
      }
    } finally {
      if (request.isCurrent()) setBusy('');
    }
  }, [backendUrl, user.token, beginRequest]);

  useEffect(() => {
    setViews([]);
    setSelectedId('');
    setName('');
    loadViews();
    return () => {
      requestRef.current.controller?.abort();
      requestRef.current.sequence += 1;
      setApplying(false);
    };
  }, [loadViews, setApplying]);

  const applyView = async (id) => {
    if (!id) {
      setSelectedId('');
      setName('');
      setNotice('');
      setError('');
      return;
    }
    setApplying(true);
    const request = beginRequest('Loading view...');
    try {
      // Fetch again because access may have changed since the list was loaded.
      const response = await axios.get(`${backendUrl}/plot-views/${encodeURIComponent(id)}`, request.options);
      if (!request.isCurrent()) return;
      await onApplyRef.current(response.data);
      if (!request.isCurrent()) return;
      setSelectedId(String(response.data.id));
      setName(response.data.name);
      setViews(previous => previous.map(view => String(view.id) === id ? response.data : view));
      setNotice(`Loaded "${response.data.name}".`);
    } catch (err) {
      if (request.isCurrent()) setError(errorMessage(err, 'Could not load this view.'));
    } finally {
      if (request.isCurrent()) {
        setBusy('');
        setApplying(false);
      }
    }
  };

  const saveView = async (update) => {
    let settings;
    try {
      settings = serializeSettings(currentSettings);
    } catch (err) {
      setError(err.message);
      setNotice('');
      return;
    }
    const request = beginRequest('Saving view...');
    const body = { name: name.trim(), ...settings };
    try {
      const response = update
        ? await axios.put(`${backendUrl}/plot-views/${encodeURIComponent(selectedId)}`, body, request.options)
        : await axios.post(`${backendUrl}/plot-views`, body, request.options);
      if (!request.isCurrent()) return;
      setViews(previous => update
        ? previous.map(view => String(view.id) === selectedId ? response.data : view)
        : [...previous, response.data]);
      setSelectedId(String(response.data.id));
      setName(response.data.name);
      setNotice(`${update ? 'Updated' : 'Saved'} "${response.data.name}".`);
    } catch (err) {
      if (request.isCurrent()) setError(errorMessage(err, 'Could not save this view.'));
    } finally {
      if (request.isCurrent()) setBusy('');
    }
  };

  const deleteView = async () => {
    const request = beginRequest('Deleting view...');
    try {
      await axios.delete(`${backendUrl}/plot-views/${encodeURIComponent(selectedId)}`, request.options);
      if (!request.isCurrent()) return;
      setViews(previous => previous.filter(view => String(view.id) !== selectedId));
      setSelectedId('');
      setName('');
      setNotice('View deleted.');
    } catch (err) {
      if (request.isCurrent()) setError(errorMessage(err, 'Could not delete this view.'));
    } finally {
      if (request.isCurrent()) setBusy('');
    }
  };

  const unavailable = disabled || Boolean(busy) || !user.token;
  const cannotSave = unavailable || !name.trim() || !currentSettings?.siteId || !currentSettings?.channelIds?.length;

  return (
    <section aria-label="Plot views" style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, display: 'grid', gap: 8 }}>
      {!collapsed && <h3 style={{ margin: 0 }}>Plot views</h3>}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <label style={fieldStyle}>
          <span>Saved views</span>
          <select
            style={{ ...inputStyle, minWidth: 180, maxWidth: '100%' }}
            value={selectedId}
            disabled={unavailable || !views.length}
            onChange={event => applyView(event.target.value)}
          >
            <option value="">Choose a saved view</option>
            {views.map(view => <option key={view.id} value={view.id}>{view.name}</option>)}
          </select>
        </label>
        {!collapsed && (
          <>
            <label style={fieldStyle}>
              <span>View name</span>
              <input
                type="text"
                value={name}
                maxLength={120}
                onChange={event => setName(event.target.value)}
                disabled={unavailable}
                style={inputStyle}
                placeholder="Name this view"
              />
            </label>
            <button type="button" style={inputStyle} disabled={cannotSave} onClick={() => saveView(false)}>Save new view</button>
            <button type="button" style={inputStyle} disabled={cannotSave || !selectedId} onClick={() => saveView(true)}>Update view</button>
            <button type="button" style={inputStyle} disabled={unavailable || !selectedId} onClick={() => applyView(selectedId)}>Reload view</button>
            <button type="button" style={inputStyle} disabled={unavailable || !selectedId} onClick={deleteView}>Delete view</button>
          </>
        )}
        <button
          type="button"
          style={inputStyle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Show plot view controls' : 'Hide plot view controls'}
          onClick={() => setCollapsed(previous => !previous)}
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </div>
      {!collapsed && <div style={{ fontSize: 12, color: '#555' }}>Views save the site, channels, date interval, PSD and legend settings. Saved dates stay fixed.</div>}
      {!collapsed && !busy && !listFailed && !views.length && <div style={{ fontSize: 12 }}>No saved views yet.</div>}
      {(!collapsed || busy) && <div role="status" style={{ fontSize: 12 }}>{busy || notice}</div>}
      {error && <div role="alert" style={{ color: '#b42318', fontSize: 13 }}>{error}</div>}
      {listFailed && <button type="button" disabled={unavailable} onClick={loadViews} style={{ justifySelf: 'start' }}>Retry loading views</button>}
    </section>
  );
}
